<template>
	<div class="container-fluid p-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory"
				title="Back to stock on hand"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div>
				<h1 class="mb-0">Receive Inventory</h1>
				<p class="text-muted mb-0">
					Check a vendor delivery into the district's inventory. The receipt is
					a movement into stock, and the product shows as on hand from that
					point.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div class="card">
			<div class="card-body">
				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<template v-else>
					<form class="row g-3 align-items-end" @submit.prevent="receive">
						<div class="col-12 col-lg-6">
							<label class="form-label" for="receiveProduct">Product</label>
							<select
								id="receiveProduct"
								v-model="productID"
								class="form-select"
								required
							>
								<option value="" disabled>Choose a catalog product</option>
								<option
									v-for="product in products"
									:key="product.product_id"
									:value="product.product_id"
								>
									{{ product.name }}
								</option>
							</select>
							<div class="form-text">
								Products come from the shared catalog, which holds the identity
								stock is keyed on.
							</div>
						</div>

						<div class="col-12 col-lg-6">
							<label class="form-label" for="receiveLocation">
								Storage location
							</label>
							<select
								id="receiveLocation"
								v-model="locationID"
								class="form-select"
								required
							>
								<option :value="null" disabled>Choose a location</option>
								<option
									v-for="choice in locationChoices"
									:key="choice.id"
									:value="choice.id"
								>
									{{ choice.label }}
								</option>
							</select>
							<div class="form-text">
								Where the product was placed. Any node of the tree holds stock.
							</div>
						</div>

						<div class="col-12 col-md-4">
							<label class="form-label" for="receiveAmount">Amount</label>
							<div class="input-group">
								<input
									id="receiveAmount"
									v-model="amountInput"
									type="number"
									min="0"
									step="any"
									inputmode="decimal"
									class="form-control"
									placeholder="0"
									required
								/>
								<span class="input-group-text">{{ unitLabel }}</span>
							</div>
							<div class="form-text">
								In the product's base unit. Set it on the
								<RouterLink to="/_/inventory/units">Units</RouterLink> page.
							</div>
						</div>

						<div class="col-12 col-md-4">
							<label class="form-label" for="receiveMethod">Method</label>
							<select id="receiveMethod" v-model="method" class="form-select">
								<option
									v-for="option in METHOD_OPTIONS"
									:key="option.value"
									:value="option.value"
								>
									{{ option.label }}
								</option>
							</select>
							<div class="form-text">
								How the amount is known: weighed, counted by container, or
								estimated.
							</div>
						</div>

						<div class="col-12 col-md-4">
							<label class="form-label" for="receiveReason">
								Note <span class="text-muted fw-normal">(optional)</span>
							</label>
							<input
								id="receiveReason"
								v-model="reason"
								type="text"
								class="form-control"
								placeholder="e.g. PO 4417"
							/>
						</div>

						<div class="col-12 d-flex justify-content-end">
							<ButtonLoading
								text="Receive"
								icon="bi bi-box-arrow-in-down"
								variant="primary"
								:loading="submitting"
								:disabled="!canReceive"
							/>
						</div>
					</form>

					<div
						v-if="missingUnit"
						class="alert alert-warning small py-2 mt-3 mb-0"
						role="alert"
					>
						No base unit is set for this product yet. Amounts recorded without
						one cannot be summed or reported — set the unit on the
						<RouterLink to="/_/inventory/units">Units</RouterLink> page first.
					</div>

					<hr class="my-4" />

					<h2 class="h6 fw-semibold">Stock at this location</h2>
					<p
						v-if="productID === '' || locationID === null"
						class="text-muted mb-0"
					>
						Choose a product and a storage location to see what is already on
						hand there.
					</p>
					<p v-else-if="currentStock" class="mb-0">
						<strong>{{ currentStock.product_name }}</strong> at
						<strong>{{ currentStock.location_name }}</strong
						>:
						<LocalizedNumber :value="currentStock.amount" />
						{{ currentStock.unit ?? "" }} — receiving adds to this.
					</p>
					<p v-else class="text-muted mb-0">
						Nothing is on hand of
						{{ productName ?? "this product" }} at this location yet.
					</p>
				</template>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import LocalizedNumber from "@/components/LocalizedNumber.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	InventoryLocation,
	InventoryStock,
	InventoryStockMethod,
	InventoryUnit,
	PesticideCatalogProduct,
	ReceiveCreate,
} from "@/type/api";

// How an amount is known is recorded on the stock itself (see
// doc/design/mvp/inventory.md, "Stock"), so the receipt states it too.
const METHOD_OPTIONS: { value: InventoryStockMethod; label: string }[] = [
	{ value: "counted", label: "Counted" },
	{ value: "weighed", label: "Weighed" },
	{ value: "estimated", label: "Estimated" },
];

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const products = ref<PesticideCatalogProduct[]>([]);
const locations = ref<InventoryLocation[]>([]);
const stock = ref<InventoryStock[]>([]);
const units = ref<InventoryUnit[]>([]);
const loading = ref(false);
const error = ref("");
const submitting = ref(false);

// An error is both the notification and the page state: the toast tells the
// operator, and the inline strip keeps the reason on screen after it dismisses.
function fail(title: string, e: any, fallback: string) {
	const message = e?.message || fallback;
	error.value = message;
	showNotification(title, message);
}

const productID = ref("");
const locationID = ref<number | null>(null);
const amountInput = ref("");
const method = ref<InventoryStockMethod>("counted");
const reason = ref("");

const amount = computed(() => {
	const parsed = Number(amountInput.value);
	return Number.isFinite(parsed) ? parsed : NaN;
});

const canReceive = computed(
	() => productID.value !== "" && locationID.value !== null && amount.value > 0,
);

const productName = computed(
	() =>
		products.value.find((product) => product.product_id === productID.value)
			?.name ?? null,
);

const selectedUnit = computed(() => {
	const row = units.value.find(
		(unit) => unit.product_catalog_id === productID.value,
	);
	if (!row || row.unit.trim() === "") return null;
	return row.unit.trim();
});

const currentStock = computed(() => {
	if (productID.value === "" || locationID.value === null) return null;
	return (
		stock.value.find(
			(row) =>
				row.product_catalog_id === productID.value &&
				row.storage_location_id === locationID.value,
		) ?? null
	);
});

// The amount is kept in the product's base unit, so the field says which one:
// the stock row's own unit, else the product's configured unit.
const unitLabel = computed(
	() => currentStock.value?.unit ?? selectedUnit.value ?? "unit not set",
);

const missingUnit = computed(
	() => productID.value !== "" && selectedUnit.value === null,
);

interface LocationChoice {
	id: number;
	label: string;
}

// The district's locations as <select> choices, each labelled by its full path
// (e.g. "Pesticide Storage > Bay 1 > Shelf A") so locations that share a leaf
// name are told apart. Retired nodes are not offered.
function locationChoicesFrom(nodes: InventoryLocation[]): LocationChoice[] {
	return nodes
		.filter((node) => node.retired === null)
		.map((node) => ({ id: node.id, label: node.path || node.name }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

const locationChoices = computed(() => locationChoicesFrom(locations.value));

async function loadStock() {
	// The stock line the form states an amount against, through the generic QUERY
	// endpoint (the legacy GET list's replacement, doc/in-progress/query.md §11
	// Phase 4), walked whole.
	const page = await apiClient.JSONQueryAll<InventoryStock>(
		"/api/inventory/stock",
	);
	stock.value = page.items;
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const [catalog, locationPage, stockPage, unitPage] = await Promise.all([
			apiClient.JSONGet<{
				products: PesticideCatalogProduct[];
				total: number;
			}>("/api/pesticide-catalog"),
			apiClient.JSONQueryAll<InventoryLocation>("/api/inventory/location"),
			apiClient.JSONQueryAll<InventoryStock>("/api/inventory/stock"),
			apiClient.JSONQueryAll<InventoryUnit>("/api/inventory/unit"),
		]);
		// A retired entry is never received: the catalog carries every product
		// ever known, and only the live ones are offered.
		products.value = (catalog.products ?? [])
			.filter((product) => product.retired === null)
			.sort((a, b) => a.name.localeCompare(b.name));
		locations.value = locationPage.items;
		stock.value = stockPage.items;
		units.value = unitPage.items;
	} catch (e: any) {
		fail("Failed to load the receiving form", e, "Please reload the page.");
	} finally {
		loading.value = false;
	}
}

async function receive() {
	if (!canReceive.value) return;
	const catalogID = productID.value;
	const storageLocationID = locationID.value;
	if (storageLocationID === null) return;
	const receivedAmount = amount.value;
	const body: ReceiveCreate = {
		product_catalog_id: catalogID,
		storage_location_id: storageLocationID,
		amount: receivedAmount,
		method: method.value,
	};
	const note = reason.value.trim();
	if (note !== "") {
		body.reason = note;
	}
	const locationName =
		locations.value.find((row) => row.id === storageLocationID)?.name ??
		"the location";
	submitting.value = true;
	try {
		await apiClient.JSONPost<InventoryStock>(
			"/api/inventory/stock/receive",
			body,
		);
		// The page states what is on hand, and the receipt changed it.
		await loadStock();
		amountInput.value = "";
		reason.value = "";
		error.value = "";
		showNotification(
			"Product received",
			`${receivedAmount} ${unitLabel.value} of ${
				productName.value ?? "product"
			} is now at ${locationName}.`,
		);
	} catch (e: any) {
		fail("Failed to receive product", e, "Please try again.");
	} finally {
		submitting.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.input-group-text {
	white-space: nowrap;
}
</style>
